import TextField from '@mui/material/TextField';
import React, { useRef, useCallback } from "react";
import styles from "./textInput.module.scss";

// Words that should prevent replacement when they appear before "resident"
const EXCLUSION_PREFIXES = ['other', 'another', 'near by', 'nearby', 'neighbouring', 'fellow'];

// Pattern to match "resident" but not "residents" (plural)
const RESIDENT_PATTERNS = [
  /\bthe resident\b/i,
  /\ba resident\b/i,
  /\bresident\b/i
];

export default function TextInput(props: TextInputProps) {
  const {handleChange, handleBlur, label, value='', error, id, touched, className, minRows=4,
         placeholder, required=false, multiline=false, disabled, onKeyDown, helperText, subLabel,
         spellCheck=true, autoComplete="off", maxLength, type='text' } = props;
  let { residentReplaceName } = props
  if(residentReplaceName) {
    // Extract content from parentheses () or quotes ""
    const parenthesesMatch = residentReplaceName.match(/\(([^)]+)\)/);
    const quotesMatch = residentReplaceName.match(/"([^"]+)"/);

    if (parenthesesMatch) {
      residentReplaceName = parenthesesMatch[1];
    } else if (quotesMatch) {
      residentReplaceName = quotesMatch[1];
    }
  }
  const inputRef = useRef<null | HTMLInputElement>(null);

  // Track the last replaced word info for backspace revert functionality
  const lastReplacementRef = useRef<{
    originalWord: string;
    replacedWith: string;
    position: number;
    reverted: boolean;
  } | null>(null);

  // Track if backspace was pressed to skip replacement
  const backspacePressedRef = useRef(false);

  const shouldSkipReplacement = useCallback((text: string, matchIndex: number): boolean => {
    // Get the text before the match to check for exclusion prefixes
    const textBefore = text.substring(0, matchIndex).toLowerCase();

    // Check if any exclusion prefix appears right before the match
    for (const prefix of EXCLUSION_PREFIXES) {
      // Check if the text ends with the prefix (with optional space)
      const prefixPattern = new RegExp(`\\b${prefix}\\s*$`, 'i');
      if (prefixPattern.test(textBefore)) {
        return true;
      }
    }
    return false;
  }, []);

  const changeWrapper = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;
    const cursorPos = event.target.selectionStart || newValue.length;

    // Check if this change is a backspace revert
    const lastReplacement = lastReplacementRef.current;
    if (lastReplacement && !lastReplacement.reverted && backspacePressedRef.current) {
      // Check if user is at or near the replaced word position and pressed backspace
      const expectedPos = lastReplacement.position + lastReplacement.replacedWith.length;
      if (cursorPos <= expectedPos && cursorPos >= lastReplacement.position) {
        // User pressed backspace on the replaced word - revert it
        const beforeReplacement = newValue.substring(0, lastReplacement.position);
        const afterReplacement = newValue.substring(cursorPos);

        // Restore original word
        newValue = beforeReplacement + lastReplacement.originalWord + afterReplacement;
        event.target.value = newValue;

        // Calculate new cursor position (at end of restored word)
        const newCursorPos = lastReplacement.position + lastReplacement.originalWord.length;
        setTimeout(() => {
          event.target.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);

        // Mark as reverted so we don't replace it again
        lastReplacementRef.current = { ...lastReplacement, reverted: true };
        backspacePressedRef.current = false;

        if (handleChange) {
          handleChange(event);
        }
        return;
      }
    }

    backspacePressedRef.current = false;

    // Check if the last word matches any resident pattern and should be replaced
    if (!residentReplaceName) {
      if (handleChange) {
        handleChange(event);
      }
      return;
    }

    // Only trigger replacement when user finishes typing the word (adds space/punctuation after)
    // This allows typing "residents" without it being replaced mid-word
    const lastChar = newValue[cursorPos - 1];
    const isWordCompleted = /[\s.,!?;:]/.test(lastChar || '');

    if (!isWordCompleted) {
      // User is still typing, don't replace yet
      if (handleChange) {
        handleChange(event);
      }
      return;
    }

    // Get the previous word (the one just completed, before the space/punctuation)
    const textBeforeLastChar = newValue.substring(0, cursorPos - 1);
    const lastSpaceBeforeWord = textBeforeLastChar.lastIndexOf(' ');
    const completedWordStart = lastSpaceBeforeWord === -1 ? 0 : lastSpaceBeforeWord + 1;
    const completedWord = textBeforeLastChar.substring(completedWordStart);

    // Check if completed word is "residents" (plural) - skip replacement
    if (/residents$/i.test(completedWord)) {
      if (handleChange) {
        handleChange(event);
      }
      return;
    }

    // Check if last replacement was reverted and user is completing that same word - skip replacement
    if (lastReplacement?.reverted) {
      const revertedWordEnd = lastReplacement.position + lastReplacement.originalWord.length;

      // Check if the reverted original word is still at the same position in the text
      // This handles both single word "resident" and multi-word "the resident" / "a resident"
      const textAtRevertedPosition = newValue.substring(
        lastReplacement.position,
        lastReplacement.position + lastReplacement.originalWord.length
      );

      if (textAtRevertedPosition.toLowerCase() === lastReplacement.originalWord.toLowerCase()) {
        // The reverted word is still there - check if user just typed space after it
        if ((cursorPos - 1) >= revertedWordEnd && (cursorPos - 1) <= revertedWordEnd + 1) {
          // User typed space right after the reverted word - don't replace it again
          lastReplacementRef.current = null;
          if (handleChange) {
            handleChange(event);
          }
          return;
        }
      }

      // User moved to a completely different position, clear the reverted state
      if (completedWordStart > revertedWordEnd + 1) {
        lastReplacementRef.current = null;
      }
    }

    // Check each pattern against the completed word context
    for (const pattern of RESIDENT_PATTERNS) {
      // We need to check a bit more context for patterns like "the resident" or "a resident"
      const contextStart = Math.max(0, completedWordStart - 4); // "the " is 4 chars
      const textToCheck = newValue.substring(contextStart, cursorPos - 1);

      const match = textToCheck.match(pattern);
      if (match) {
        const matchIndexInContext = match.index!;
        const matchIndexInFullText = contextStart + matchIndexInContext;

        // Check if this match is part of the completed word
        if (matchIndexInFullText + match[0].length < completedWordStart) {
          continue; // Match is before the completed word, skip it
        }

        // Check exclusion prefixes
        if (shouldSkipReplacement(newValue, matchIndexInFullText)) {
          continue;
        }

        // Perform replacement
        const beforeMatch = newValue.substring(0, matchIndexInFullText);
        const afterMatchText = newValue.substring(matchIndexInFullText + match[0].length);
        const lengthDiff = residentReplaceName.length - match[0].length;
        newValue = beforeMatch + residentReplaceName + afterMatchText;

        // Store replacement info for potential backspace revert
        lastReplacementRef.current = {
          originalWord: match[0],
          replacedWith: residentReplaceName,
          position: matchIndexInFullText,
          reverted: false
        };

        event.target.value = newValue;

        // Restore cursor position adjusted for the length difference
        const newCursorPos = cursorPos + lengthDiff;
        setTimeout(() => {
          event.target.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
        break;
      }
    }

    if (handleChange) {
      handleChange(event);
    }
  }, [handleChange, residentReplaceName, shouldSkipReplacement]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      backspacePressedRef.current = true;
    }

    if (onKeyDown) {
      onKeyDown(event);
    }
  }, [onKeyDown]);

  return(
    <div className={`${styles.textInputWrapper} ${className} ${disabled ? styles.textInputWrapper_disabled : ''}`}>
      {label
        ? <div className={styles.textInputWrapper_label}>{label}{required ? <span>*</span> : ''}</div>
        : <></>}

      {subLabel
        ? <div className={styles.textInputWrapper_subLabel}>{subLabel}</div>
        : <></>}

      <TextField
        className={styles.input}
        InputProps={{
          classes: {
              root: `${styles.input__field_input} ${multiline ? styles.input__field_input_multiLine : ''}`,
              focused: styles.input__field_active,
              error: styles.input__field_error,
              disabled: styles.input__field_disabled,
          },
        }}
        inputProps={{
          maxLength: maxLength ? maxLength : 1000000,
        }}
        disabled={disabled}
        multiline={multiline}
        minRows={minRows}
        fullWidth
        value={value}
        spellCheck={spellCheck}
        onChange={residentReplaceName ? changeWrapper : handleChange}
        onBlur={handleBlur}
        error={Boolean(error && touched)}
        placeholder={placeholder}
        name={id}
        autoComplete={autoComplete}
        id={id}
        ref={inputRef}
        variant='outlined'
        type={type}
        onKeyDown={residentReplaceName ? handleKeyDown : onKeyDown}
        
        helperText={helperText}
        FormHelperTextProps={{ classes: { root: styles.helperText } }}
      />

      {touched && error
        ? <div className={styles.textInputWrapper_error}>{error}</div>
        : <></>}

      {
        maxLength ?
          <div className={`${styles.textInputWrapper_length} ${touched && error && styles.textInputWrapper_length_error}`}>
            <span className={styles.text}>{value.length}</span>

            <span className={styles.text}>/</span>
            
            <span className={styles.text}>{maxLength}</span>
          </div>
          :
          <></>
      }
    </div>
  )
}

interface TextInputProps {
  /** Input id */
  id: string,

  /** Input label */
  label: string,

  /** Input sub-label */
  subLabel?: string,

  /** Input value */
  value?: string,

  /** Input placeholder */
  placeholder?: string,

  /** change event handler */
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,

  /** blur event handler */
  handleBlur?: (e: React.FocusEvent) => void,

  /** keyDown handler */
  onKeyDown?: (e: React.KeyboardEvent) => void,

  /** Input error message */
  error?: string,

  /** Input touched */
  touched?: boolean,

  /** just for view, use Yup to validate 
   * @default false
  */
  required?: boolean,

  /** multiline flag
   * @default false
   */
  multiline?: boolean,

  /** Min row amount (if multiline true) */
  minRows?: number,

  /** Max string length */
  maxLength?: number,

  /** Disabled input
   * @default false
  */
  disabled?: boolean,

  /** Input type
   * @default text
   */
  type?: string,

  /** Number step
   * @default null
   */
  step?: string,

  /** className for container wrapping TextInput component */
  className?: string,
  
  /** Helper text */
  helperText?: string,

  /** Text spell check 
   * @default false
  */
  spellCheck?: boolean,

  /** Fill the text field with the text that was previously entered in it.
   * @default off
  */
  autoComplete?: 'on' | 'off' | 'string'

  /** Replace string "resident"/"the resident" with actual name. Contains resident name
   * @default null
   */
  residentReplaceName?: string
}